import {
  type WorkspaceSearchQuery,
  workspaceSearchQuery,
  WorkspaceSearchSourceKind,
} from '@affine/graphql';
import type { I18nString } from '@affine/i18n';
import { EmailIcon } from '@blocksuite/icons/rc';
import { permissionSnapshotPartitionIndex } from '@streamlain/permission-snapshot-contract';
import { type Effect, effect, Entity, LiveData } from '@toeverything/infra';
import { truncate } from 'lodash-es';
import {
  catchError,
  concat,
  debounceTime,
  firstValueFrom,
  from,
  map,
  type Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type {
  DocsSearchService,
  SearchPartitionSyncService,
} from '../../docs-search';
import { focusQueryTerms } from '../../docs-search/services/query-terms';
import type { WorkspaceService } from '../../workspace';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchGroup } from '../types/group';
import type { QuickSearchItem } from '../types/item';

export type MailSearchResult =
  WorkspaceSearchQuery['workspace']['workspaceSearch'][number];

export interface MailPayload {
  route: string;
  messageId?: string | null;
  accountId?: string | null;
  folderId?: string | null;
  subject?: string | null;
  from?: string | null;
  receivedAt?: string | null;
}

/** One mail of the local search index, as the mail search reads it. */
export interface LocalMailHit {
  /** The mailbox page whose right is the right to read the mail. */
  docId: string;
  messageId: string;
  accountId: string;
  folderId: string | null;
  subject: string;
  participants: string;
  snippet: string;
  content: string;
  receivedAt: string;
  score: number;
}

const MAIL_SEARCH_LIMIT = 20;
const MAIL_SEARCH_DEBOUNCE_MS = 350;
const MAIL_ITEM_PREFIX = 'mail-message:';
const LOCAL_MAIL_FIELDS = [
  'docId',
  'messageId',
  'accountId',
  'folderId',
  'subject',
  'participants',
  'snippet',
  'content',
  'receivedAt',
] as const;

function mailGroup(query: string): QuickSearchGroup {
  return {
    id: 'mail',
    label: {
      i18nKey: 'com.streamlain.quicksearch.group.mail',
      options: { query: truncate(query) },
    },
    score: 6,
  };
}

/** The route that opens a mail in the mail view. */
export function mailRoute(mail: {
  messageId: string;
  accountId: string;
  folderId: string | null;
}) {
  const params = new URLSearchParams();
  params.set('messageId', mail.messageId);
  params.set('accountId', mail.accountId);
  if (mail.folderId) params.set('folderId', mail.folderId);
  return `/mail?${params.toString()}`;
}

export function mapWorkspaceSearchMailResults(
  query: string,
  results: readonly MailSearchResult[]
): QuickSearchItem<'mail', MailPayload>[] {
  const group = mailGroup(query);

  return results
    .filter(result => result.kind === WorkspaceSearchSourceKind.mail_message)
    .map(result => ({
      id: result.id,
      source: 'mail' as const,
      group,
      label: {
        title: result.title,
        subTitle: result.snippet || result.subtitle || undefined,
      },
      score: result.score,
      icon: EmailIcon,
      timestamp: parseSearchTimestamp(result.receivedAt),
      badge: {
        i18nKey: result.attachmentHits.length
          ? 'com.streamlain.quicksearch.sourceType.mailWithAttachments'
          : 'com.streamlain.quicksearch.sourceType.mail',
        options: { count: result.attachmentHits.length },
      },
      payload: {
        route: result.openTarget.route,
        messageId: result.mailMessageId,
        accountId: result.mailAccountId,
        folderId: result.mailFolderId,
        subject: result.mailSubject,
        from: result.mailFrom,
        receivedAt: result.receivedAt,
      },
    }));
}

/** Mail hits of the local index as quick search items, like server hits. */
export function mapLocalMailHits(
  query: string,
  hits: readonly LocalMailHit[]
): QuickSearchItem<'mail', MailPayload>[] {
  const group = mailGroup(query);
  return hits.map(hit => ({
    id: `${MAIL_ITEM_PREFIX}${hit.messageId}`,
    source: 'mail' as const,
    group,
    label: {
      title: hit.subject,
      subTitle:
        focusQueryTerms(hit.content, query, { before: 20, after: 100 }) ??
        (hit.snippet || hit.participants || undefined),
    },
    score: hit.score,
    icon: EmailIcon,
    timestamp: parseSearchTimestamp(hit.receivedAt),
    badge: { i18nKey: 'com.streamlain.quicksearch.sourceType.mail' },
    payload: {
      route: mailRoute(hit),
      messageId: hit.messageId,
      accountId: hit.accountId,
      folderId: hit.folderId,
      subject: hit.subject,
      receivedAt: hit.receivedAt,
    },
  }));
}

/**
 * Offline the device searches only the mails of the mail period; this row
 * under the mail hits says that older mails are searchable online only.
 */
export function offlineOlderMailsHint(
  query: string
): QuickSearchItem<'mail', MailPayload> {
  return {
    id: `${MAIL_ITEM_PREFIX}offline-older`,
    source: 'mail' as const,
    group: mailGroup(query),
    label: { i18nKey: 'com.streamlain.quicksearch.mail.offlineOlder' },
    score: 0,
    row: 'more',
    disabled: true,
    payload: { route: '/mail' },
    beforeSubmit: () => false,
  };
}

/**
 * Searches mails local first: the local index answers the mails of every
 * partition this device holds current, the server answers the rest in the
 * same list, each mail from one source. Offline the local index answers
 * alone, also for partitions the device could not compare.
 */
export class MailQuickSearchSession
  extends Entity
  implements QuickSearchSession<'mail', MailPayload>
{
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly searchPartitionSync: SearchPartitionSyncService,
    private readonly docsSearch: DocsSearchService
  ) {
    super();
  }

  items$ = new LiveData<QuickSearchItem<'mail', MailPayload>[]>([]);
  error$ = new LiveData<any>(null);
  isLoading$ = new LiveData(false);
  status$ = new LiveData<I18nString | null>(null);

  private readonly mailUnavailableStatus: I18nString = {
    i18nKey: 'com.streamlain.quicksearch.status.mail-unavailable',
  };

  private readonly isOnline = () =>
    typeof navigator === 'undefined' || navigator.onLine;

  private readonly queryEffect = effect(
    tap((query: string) => {
      this.error$.next(null);
      this.items$.next([]);
      this.status$.next(null);
      this.isLoading$.next(Boolean(query.trim()));
    }),
    debounceTime(MAIL_SEARCH_DEBOUNCE_MS),
    switchMap((query: string) => {
      const keyword = query.trim();
      if (!keyword) return of({ items: [], settled: true });
      const server = this.workspaceServerService.server;
      const online = !!server && this.isOnline();
      // Online, a partition the device has not compared is the server's.
      const current = online
        ? (this.searchPartitionSync.searchSources()?.currentPartitions ??
          new Set<number>())
        : null;
      return from(this.searchLocal(keyword, current)).pipe(
        switchMap(local => {
          const localItems = mapLocalMailHits(keyword, local);
          if (!server) return of({ items: localItems, settled: true });
          if (!online)
            return of({
              items: [...localItems, offlineOlderMailsHint(keyword)],
              settled: true,
            });
          return concat(
            of({ items: localItems, settled: false }),
            this.searchServer(server, keyword, current ?? new Set()).pipe(
              map(remote => ({
                items: [...localItems, ...remote],
                settled: true,
              })),
              catchError(error => {
                console.warn(
                  '[MailQuickSearchSession] Mail search unavailable',
                  error
                );
                this.status$.next(this.mailUnavailableStatus);
                return of({ items: localItems, settled: true });
              })
            )
          );
        }),
        catchError(error => {
          console.warn(
            '[MailQuickSearchSession] Local mail search failed',
            error
          );
          return of({ items: [], settled: true });
        })
      );
    }),
    tap(({ items, settled }) => {
      this.items$.next(items);
      if (settled) this.isLoading$.next(false);
    })
  ) as Effect<string>;

  /** Local mail hits; `current` limits them to compared partitions. */
  private async searchLocal(
    keyword: string,
    current: ReadonlySet<number> | null
  ): Promise<LocalMailHit[]> {
    const nodes = await firstValueFrom(
      this.docsSearch.searchMails$(keyword, MAIL_SEARCH_LIMIT)
    );
    return nodes
      .map(node => {
        const field = (key: (typeof LOCAL_MAIL_FIELDS)[number]) =>
          firstField(node.fields[key]);
        return {
          docId: field('docId'),
          messageId: field('messageId'),
          accountId: field('accountId'),
          folderId: field('folderId') || null,
          subject: field('subject'),
          participants: field('participants'),
          snippet: field('snippet'),
          content: field('content'),
          receivedAt: field('receivedAt'),
          score: node.score,
        };
      })
      .filter(
        hit =>
          !current ||
          current.has(permissionSnapshotPartitionIndex('doc', hit.docId))
      );
  }

  /** Server hits of the mails the local index does not answer. */
  private searchServer(
    server: NonNullable<WorkspaceServerService['server']>,
    keyword: string,
    current: ReadonlySet<number>
  ): Observable<QuickSearchItem<'mail', MailPayload>[]> {
    return from(
      server.gql({
        query: workspaceSearchQuery,
        variables: {
          id: this.workspaceService.workspace.id,
          input: {
            keyword,
            limit: MAIL_SEARCH_LIMIT,
            sourceKinds: [WorkspaceSearchSourceKind.mail_message],
          },
        },
      }) as Promise<WorkspaceSearchQuery>
    ).pipe(
      switchMap(result => {
        const items = mapWorkspaceSearchMailResults(
          keyword,
          result.workspace.workspaceSearch
        );
        return from(this.answeredLocally(items, current)).pipe(
          map(local =>
            items.filter(item => !local.has(item.payload.messageId ?? ''))
          )
        );
      })
    );
  }

  /** Message ids of server hits whose mail the local index holds current. */
  private async answeredLocally(
    items: readonly QuickSearchItem<'mail', MailPayload>[],
    current: ReadonlySet<number>
  ): Promise<Set<string>> {
    const messageIds = items.flatMap(item =>
      item.payload.messageId ? [item.payload.messageId] : []
    );
    if (messageIds.length === 0 || current.size === 0) return new Set();
    const nodes = await firstValueFrom(this.docsSearch.mails$(messageIds));
    return new Set(
      nodes
        .filter(node =>
          current.has(
            permissionSnapshotPartitionIndex(
              'doc',
              firstField(node.fields.docId)
            )
          )
        )
        .map(node => firstField(node.fields.messageId))
    );
  }

  query(query: string) {
    this.queryEffect(query);
  }

  override dispose(): void {
    this.queryEffect.unsubscribe();
  }
}

function firstField(value: string | string[] | undefined) {
  return (Array.isArray(value) ? value[0] : value) ?? '';
}

function parseSearchTimestamp(value: string | null | undefined) {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}
